import React, { useMemo } from 'react';
import { hot } from 'react-hot-loader/root';
import {
  CssBaseline,
  createMuiTheme,
  ThemeProvider,
  Box,
  IconButton,
} from '@material-ui/core';
import { NavLink, Router } from 'react-router-dom';
import history from '@/common/history';
import Routes from '@/routes';
import { useAuth0 } from '@cmr/react-auth0';
import { Loading, NoAccess, UpdateNotify, AppBar } from '@cmr/react-ui-common';
import { ClientsProvider } from '@cmr/react-clients-api';
import { OcrMapsProvider } from '@/features/ocr-maps/components/context';
import { SignalRProvider } from '@/features/signalr/context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const App: React.FC = () => {
  const { hasPermission, isAuthenticated, loading, unauthorized } = useAuth0();
  const showMapLink = hasPermission('ocrmaps:view');
  const showClientsLink = hasPermission('admin:clients');

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: {
          primary: { main: '#B42D34' },
          secondary: { main: '#666666' },
          type: 'dark',
        },
      }),
    [],
  );

  if (unauthorized) return <NoAccess theme={theme} displayName='Invoicing' />;

  return (
    <SignalRProvider
      negotiateEndpoint={`${process.env['REACT_APP_OCR_API_ENDPOINT']}hub`}
    >
      <ClientsProvider
        endpoint={process.env.REACT_APP_CLIENTS_API_ENDPOINT!}
        loadOnInit
      >
        <OcrMapsProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <UpdateNotify />
            {(loading || !isAuthenticated) && <Loading />}
            {!loading && isAuthenticated && (
              <Router history={history}>
                <Box height='100vh' display='flex' flexDirection='column'>
                  <AppBar appName='Police Reports'>
                    {showMapLink && (
                      <NavLink to='/ocr-maps'>
                        <IconButton aria-label='OCR Maps'>
                          <FontAwesomeIcon icon={['fad', 'map']} />
                        </IconButton>
                      </NavLink>
                    )}
                    {showClientsLink && (
                      <NavLink to='/clients'>
                        <IconButton aria-label='Configuration'>
                          <FontAwesomeIcon icon={['fad', 'cog']} />
                        </IconButton>
                      </NavLink>
                    )}
                  </AppBar>
                  <Routes />
                </Box>
              </Router>
            )}
          </ThemeProvider>
        </OcrMapsProvider>
      </ClientsProvider>
    </SignalRProvider>
  );
};

export default process.env.NODE_ENV === 'development' ? hot(App) : App;
